import { z } from "zod";

/** Question ids are survey-scoped slugs, so a future survey can define its own. */
export const ArenaSurveyQuestionIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9_]+$/, "question id must be lowercase alphanumeric or underscore");
export type ArenaSurveyQuestionId = z.infer<typeof ArenaSurveyQuestionIdSchema>;

export const ArenaSurveyQuestionSchema = z.object({
  id: ArenaSurveyQuestionIdSchema,
  text: z.string().trim().min(1).max(200),
});
export type ArenaSurveyQuestionDto = z.infer<typeof ArenaSurveyQuestionSchema>;

export const ARENA_SURVEY_MAX_QUESTIONS = 10;

/** An ordered, non-empty question set with no duplicate ids. Order = array order. */
const QuestionsArraySchema = z
  .array(ArenaSurveyQuestionSchema)
  .min(1)
  .max(ARENA_SURVEY_MAX_QUESTIONS)
  .superRefine((qs, ctx) => {
    if (new Set(qs.map((q) => q.id)).size !== qs.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "question ids must be unique" });
    }
  });

export const ArenaSurveyConfigSchema = z.object({ questions: QuestionsArraySchema });
export type ArenaSurveyConfigDto = z.infer<typeof ArenaSurveyConfigSchema>;

export const UpdateArenaSurveyConfigBodySchema = z.object({ questions: QuestionsArraySchema });
export type UpdateArenaSurveyConfigBody = z.infer<typeof UpdateArenaSurveyConfigBodySchema>;

/** How a stored response resolved. A row exists only once the user answered or skipped. */
export const ArenaSurveyOutcomeSchema = z.enum(["COMPLETED", "SKIPPED"]);
export type ArenaSurveyOutcome = z.infer<typeof ArenaSurveyOutcomeSchema>;

export const ArenaSurveyStateSchema = z.object({
  // True once a response row exists for the ACTIVE survey (answered OR skipped).
  // Scoped per survey, so activating a new one re-opens it for everyone.
  hasAnswered: z.boolean(),
  // True when the user has an entry in a non-onboarding competition: the
  // `campaignName` one when the request names it, else any. Same check submit
  // and skip enforce, so a true here never 403s there.
  hasParticipated: z.boolean(),
  // The active survey's questions, in display order.
  questions: QuestionsArraySchema,
});
export type ArenaSurveyStateDto = z.infer<typeof ArenaSurveyStateSchema>;

export const ARENA_SURVEY_COMMENT_MAX = 500;

/** At or below this score the comment stops being optional. */
export const ARENA_SURVEY_LOW_SCORE_MAX = 3;

/** True when at least one answer is low enough to require an explanation. */
export const isLowArenaSurveyScore = (scores: number[]): boolean =>
  scores.some((score) => score <= ARENA_SURVEY_LOW_SCORE_MAX);

/**
 * The competition being rated. Sent by the client because only it knows which
 * recap the modal opened on; the server rejects a name the user never played
 * rather than substituting one.
 */
const RatedCampaignNameSchema = z.string().trim().min(1).max(255);

export const SubmitArenaSurveyBodySchema = z
  .object({
    // Must cover the active survey's questions exactly, checked server-side
    // against that survey, since the set is no longer a compile-time constant.
    answers: z
      .array(z.object({ questionId: ArenaSurveyQuestionIdSchema, score: z.number().int().min(1).max(5) }))
      .min(1)
      .max(ARENA_SURVEY_MAX_QUESTIONS)
      .superRefine((ans, ctx) => {
        if (new Set(ans.map((a) => a.questionId)).size !== ans.length) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "each question may be answered only once" });
        }
      }),
    comment: z.string().trim().max(ARENA_SURVEY_COMMENT_MAX).optional(),
    campaignName: RatedCampaignNameSchema.optional(),
  })
  .superRefine((body, ctx) => {
    if (isLowArenaSurveyScore(body.answers.map((a) => a.score)) && !body.comment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["comment"],
        message: `a comment is required when any score is ${ARENA_SURVEY_LOW_SCORE_MAX} or below`,
      });
    }
  });
export type SubmitArenaSurveyBody = z.infer<typeof SubmitArenaSurveyBodySchema>;

export const SkipArenaSurveyBodySchema = z.object({ campaignName: RatedCampaignNameSchema.optional() });
export type SkipArenaSurveyBody = z.infer<typeof SkipArenaSurveyBodySchema>;

export const GetArenaSurveyStateQuerySchema = z.object({ campaignName: RatedCampaignNameSchema.optional() });
export type GetArenaSurveyStateQueryParams = z.infer<typeof GetArenaSurveyStateQuerySchema>;

/** One stored response, as the Solo CMS reads it. Skips carry no scores. */
export const ArenaSurveyResponseDtoSchema = z.object({
  id: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  outcome: ArenaSurveyOutcomeSchema,
  /** Null when the client didn't say which competition it was rating. */
  campaignName: z.string().nullable(),
  /** Scored answers, labelled with the question wording that was live. */
  answers: z.array(
    z.object({ questionId: ArenaSurveyQuestionIdSchema, question: z.string(), score: z.number().int() }),
  ),
  comment: z.string().nullable(),
  createdAt: z.string(),
});
export type ArenaSurveyResponseDto = z.infer<typeof ArenaSurveyResponseDtoSchema>;

export const ARENA_SURVEY_RESPONSES_MAX_PAGE_SIZE = 100;

/** Filters for the CMS responses list. `outcome` omitted = both. */
export const ListArenaSurveyResponsesQuerySchema = z.object({
  page: z.coerce.number().int().nonnegative(),
  // Capped so the CMS cannot ask for an unbounded page.
  size: z.coerce.number().int().positive().max(ARENA_SURVEY_RESPONSES_MAX_PAGE_SIZE),
  outcome: ArenaSurveyOutcomeSchema.optional(),
  /**
   * Only responses carrying free text: the ones worth reading first. Parsed
   * from the literal, never `z.coerce.boolean()`: that reads "false" as true.
   */
  withCommentOnly: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
});
export type ListArenaSurveyResponsesQuery = z.infer<typeof ListArenaSurveyResponsesQuerySchema>;

/** Paginated response for `GET /v1/cms/arena-survey-settings/responses`. Page is 0-indexed. */
export const ListArenaSurveyResponsesResponseSchema = z.object({
  items: ArenaSurveyResponseDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListArenaSurveyResponsesResponse = z.infer<typeof ListArenaSurveyResponsesResponseSchema>;
